import type {
  GameSaveCampaign, GameSaveError, GameSaveOptions, IGameSaveManager,
  LoadGameResult, SaveGameResult,
} from './IGameSaveManager';
import type { IGameSaveSlotSelector } from './GameSaveSlotSelector';
import type { GameSaveCampaignConfiguration, HostCampaignConfig } from '@playcastdotio/Messaging';
import { gameSaveCampaignConfigurationSchema } from '@playcastdotio/Messaging';

/** Internal native execution input; campaign settings come from the assigned dashboard payload. */
export interface NativeGameSaveOptions extends GameSaveOptions {
  slotId: string;
  configuration?: GameSaveCampaignConfiguration;
}
type PinnedGameSaveOptions = GameSaveOptions & { configuration?: GameSaveCampaignConfiguration };
export interface IGameSaveNativeExecutor {
  executeSaveGame(options: NativeGameSaveOptions): Promise<SaveGameResult>;
  executeLoadGame(options: NativeGameSaveOptions): Promise<LoadGameResult>;
}
export interface GameSaveManagerDependencies {
  slotSelector: IGameSaveSlotSelector;
  nativeExecutor: IGameSaveNativeExecutor;
  configurationProvider?: {
    readCampaignConfiguration(campaign: GameSaveCampaign): GameSaveCampaignConfiguration | undefined;
  };
}

export class GameSaveOperationError extends Error {
  constructor(public readonly code: GameSaveError['code'], message: string, public readonly retryable = false) {
    super(message);
    this.name = 'GameSaveOperationError';
  }
}

/** Frontend owns ordered host intent; Playjector owns file/process work and safety gates. */
export class GameSaveManager implements IGameSaveManager {
  private previousGameSaveOperation: Promise<void> = Promise.resolve();
  constructor(private readonly dependencies: GameSaveManagerDependencies) {}

  saveGame(options: GameSaveOptions): Promise<SaveGameResult> {
    let pinnedOptions: PinnedGameSaveOptions;
    try { pinnedOptions = this.projectGameSaveOptions(options); }
    catch (cause) { return Promise.resolve({ status: 'failed', error: this.toGameSaveError(cause) }); }
    return this.executeGameSaveOperationSerially(async () => {
      try {
        return await this.dependencies.nativeExecutor.executeSaveGame(await this.selectNativeOptions(pinnedOptions));
      } catch (cause) { return { status: 'failed', error: this.toGameSaveError(cause) }; }
    });
  }

  loadGame(options: GameSaveOptions): Promise<LoadGameResult> {
    let pinnedOptions: PinnedGameSaveOptions;
    try { pinnedOptions = this.projectGameSaveOptions(options); }
    catch (cause) { return Promise.resolve({ status: 'failed', error: this.toGameSaveError(cause) }); }
    return this.executeGameSaveOperationSerially(async () => {
      try {
        return await this.dependencies.nativeExecutor.executeLoadGame(await this.selectNativeOptions(pinnedOptions));
      } catch (cause) { return { status: 'failed', error: this.toGameSaveError(cause) }; }
    });
  }

  private projectGameSaveOptions(options: GameSaveOptions): PinnedGameSaveOptions {
    const suppliedCampaign = options?.campaign;
    const launchPackage = suppliedCampaign?.launchPackage;
    let campaign: GameSaveCampaign;
    if (launchPackage?.launcher === 'steam' && typeof launchPackage.launchId === 'string' &&
      /^[1-9][0-9]{0,9}$/.test(launchPackage.launchId)) {
      campaign = { campaignId: suppliedCampaign.campaignId,
        launchPackage: { launcher: 'steam', launchId: launchPackage.launchId } };
    } else if (launchPackage?.launcher === 'android' && typeof launchPackage.package === 'string' &&
      launchPackage.package.length <= 256 &&
      /^[A-Za-z][A-Za-z0-9_]*(?:\.[A-Za-z][A-Za-z0-9_]*)+$/.test(launchPackage.package)) {
      campaign = { campaignId: suppliedCampaign.campaignId,
        launchPackage: { launcher: 'android', package: launchPackage.package } };
    } else throw new GameSaveOperationError('invalid_target', 'Campaign game identity is invalid');
    if (typeof campaign.campaignId !== 'string' || !campaign.campaignId.trim() ||
      campaign.campaignId.length > 256 || typeof options.requireStopped !== 'boolean') {
      throw new GameSaveOperationError('invalid_target', 'Campaign game identity is invalid');
    }
    const suppliedConfiguration = this.dependencies.configurationProvider?.readCampaignConfiguration(campaign);
    const parsedConfiguration = suppliedConfiguration === undefined ? undefined
      : gameSaveCampaignConfigurationSchema.safeParse(suppliedConfiguration);
    if (parsedConfiguration && !parsedConfiguration.success) {
      throw new GameSaveOperationError('invalid_target', 'Assigned campaign game save settings are invalid');
    }
    const configuration = parsedConfiguration?.success ? parsedConfiguration.data : undefined;
    return configuration === undefined ? { campaign, requireStopped: options.requireStopped }
      : { campaign, requireStopped: options.requireStopped, configuration };
  }

  private async selectNativeOptions(options: PinnedGameSaveOptions): Promise<NativeGameSaveOptions> {
    const slotId = await this.dependencies.slotSelector.selectGameSaveSlot(options.campaign);
    if (typeof slotId !== 'string' || !/^[A-Za-z0-9][A-Za-z0-9_.:-]{0,127}$/.test(slotId)) {
      throw new GameSaveOperationError('invalid_target', 'Selected game save slot is invalid');
    }
    return { ...options, slotId };
  }

  private async executeGameSaveOperationSerially<TResult>(operation: () => Promise<TResult>): Promise<TResult> {
    const preceding = this.previousGameSaveOperation;
    let release!: () => void;
    this.previousGameSaveOperation = new Promise<void>((finish) => { release = finish; });
    await preceding;
    try { return await operation(); } finally { release(); }
  }

  private toGameSaveError(cause: unknown): GameSaveError {
    return cause instanceof GameSaveOperationError
      ? { code: cause.code, retryable: cause.retryable, message: cause.message }
      : { code: 'internal', retryable: false, message: 'Game save operation failed' };
  }
}

export interface ICampaignGameSaveConfigurationStore {
  updateCampaignConfiguration(campaign: HostCampaignConfig): void;
  readCampaignConfiguration(campaign: GameSaveCampaign): GameSaveCampaignConfiguration | undefined;
}

/** Pins dashboard settings to the assigned game without extending save/load caller options. */
export class CampaignGameSaveConfigurationStore implements ICampaignGameSaveConfigurationStore {
  private assigned: { campaignId: string; gameIdentity: string; configuration?: GameSaveCampaignConfiguration } | null = null;

  updateCampaignConfiguration(campaign: HostCampaignConfig): void {
    const launch = campaign.launchPackage;
    const gameIdentity = launch.launcher === 'steam' ? `steam:${launch.launchId}`
      : launch.launcher === 'android' ? `android:${launch.package}` : launch.launcher;
    // Drop the previous assignment before validation: invalid replacement settings cannot reuse it.
    this.assigned = null;
    const configuration = campaign.gameSave === undefined ? undefined
      : gameSaveCampaignConfigurationSchema.parse(campaign.gameSave);
    this.assigned = { campaignId: campaign.campaignId, gameIdentity, configuration };
  }

  readCampaignConfiguration(campaign: GameSaveCampaign): GameSaveCampaignConfiguration | undefined {
    if (!this.assigned) {
      throw new GameSaveOperationError('invalid_target', 'No validated campaign game save assignment is available');
    }
    const launch = campaign.launchPackage;
    const gameIdentity = launch.launcher === 'steam' ? `steam:${launch.launchId}` : `android:${launch.package}`;
    if (this.assigned.campaignId !== campaign.campaignId || this.assigned.gameIdentity !== gameIdentity) {
      throw new GameSaveOperationError('invalid_target', 'Game save settings do not match the assigned campaign game');
    }
    return this.assigned.configuration === undefined ? undefined
      : gameSaveCampaignConfigurationSchema.parse(this.assigned.configuration);
  }
}
